import {
  Backdrop,
  Box,
  ClickAwayListener,
  Divider,
  Icon,
  Stack,
  Tooltip,
  TooltipProps,
} from '@mui/material';
import { useFlag } from '@schooly/hooks/use-flag';
import { BellIcon, TypographyWithOverflowHint } from '@schooly/style';
import { Fragment, ReactNode, useCallback } from 'react';

import { DropdownSelectRow } from '../DropdownSelectRow';
import { ExpandedSelect } from '../ExpandedSelect';

const DROPDOWN_WIDTH = 350;

type DropdownAttendanceCodesProps<T> = {
  width?: number;
  onSelect: (v: T) => void;
  selectedOption?: T;
  options: T[][];
  disabled?: boolean;
  children: (opened: boolean) => ReactNode;
} & Omit<TooltipProps, 'title' | 'children' | 'onSelect'>;

export const DropdownAttendanceCodes = <
  T extends { label: string; id: string; withNotification?: boolean; avatar: ReactNode },
>({
  width = DROPDOWN_WIDTH,
  options,
  selectedOption,
  onSelect,
  disabled,
  children,
  ...tooltipProps
}: DropdownAttendanceCodesProps<T>) => {
  const [opened, open, close] = useFlag();

  const handleSelect = useCallback(
    (option: T) => {
      if (option.id === selectedOption?.id) {
        return;
      }

      onSelect(option);
      close();
    },
    [close, onSelect, selectedOption?.id],
  );

  const renderContent = useCallback(() => {
    return (
      <Stack gap={0.25} p={1}>
        {options.map((sections, index, array) => (
          <Fragment key={sections[0]?.id ?? index}>
            {sections.map((option) => (
              <DropdownSelectRow
                key={option.id}
                isSelected={selectedOption?.id === option.id}
                onClick={() => handleSelect(option)}
              >
                <Stack direction="row" gap={1} alignItems="center">
                  {option.avatar}
                  <TypographyWithOverflowHint variant="h3" color="inherit">
                    {option.label}
                  </TypographyWithOverflowHint>
                  {!!option?.withNotification && (
                    <Icon inverse sx={(theme) => ({ fontSize: theme.spacing(1.5) })}>
                      <BellIcon />
                    </Icon>
                  )}
                </Stack>
              </DropdownSelectRow>
            ))}
            {index !== array.length - 1 && <Divider />}
          </Fragment>
        ))}
      </Stack>
    );
  }, [handleSelect, options, selectedOption?.id]);

  return (
    <>
      <Backdrop open={opened} invisible sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }} />
      <ClickAwayListener onClickAway={close}>
        <div>
          <Tooltip
            onClose={close}
            open={opened}
            placement="bottom-start"
            componentsProps={{
              tooltip: {
                sx: (theme) => ({
                  width: width,
                  maxWidth: width,
                  border: `1px solid ${theme.palette.common.light3}`,
                  padding: 0,
                  overflow: 'hidden',
                }),
              },
              popper: {
                sx: (theme) => ({
                  '&.MuiTooltip-popper[data-popper-placement*="bottom"] .MuiTooltip-tooltip': {
                    margin: (theme) => theme.spacing(-3.5, 0, 0, -1),
                  },
                  '&.MuiTooltip-popper[data-popper-placement*="top"] .MuiTooltip-tooltip': {
                    margin: theme.spacing(0, 0, -3.5, -1),
                  },
                }),
              },
            }}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            title={
              <ExpandedSelect
                width={width}
                hasSelectedValue={!!selectedOption}
                onClose={close}
                renderContent={renderContent}
              >
                <Stack direction="row" gap={1} alignItems="center">
                  {selectedOption?.avatar}
                  <TypographyWithOverflowHint variant="h3" color="inherit">
                    {selectedOption?.label}
                  </TypographyWithOverflowHint>
                  {!!selectedOption?.withNotification && (
                    <Icon inverse sx={(theme) => ({ fontSize: theme.spacing(1.5) })}>
                      <BellIcon />
                    </Icon>
                  )}
                </Stack>
              </ExpandedSelect>
            }
            {...tooltipProps}
          >
            <Box
              flexDirection="row"
              sx={{
                cursor: disabled ? 'default' : 'pointer',
                position: 'relative',
                zIndex: (theme) => (opened ? theme.zIndex.drawer + 2 : undefined),
              }}
              alignItems="center"
              onClick={() => !disabled && open()}
            >
              {children(opened)}
            </Box>
          </Tooltip>
        </div>
      </ClickAwayListener>
    </>
  );
};
